import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import type {
  SkillDataTableItem,
  SkillListStateFilter,
} from '@kirin/api-client/contracts/skills';
import { SKILL_FILTER_OPTION_FIELDS } from '@kirin/api-client/contracts/skills';
import {
  MCP_RISK_LEVELS,
  mcpRiskLevelLabels,
  type MCPRiskLevel,
} from '@kirin/api-client/contracts/mcp';
import { McpRiskFilterOption } from '@/components/shared/mcp/McpRiskBadge';
import { NameCell, ProjectsCell, UsersCell } from './CellRenderers';
import { SkillRiskCell } from './SkillRiskCell';
import { SkillStateCell } from './SkillStateCell';
import { SkillGroupStateCell } from './SkillGroupStateCell';
import { SKILL_FILTER_URL_KEYS } from './constants';
import { fetchSkillFilterOptions, getSkillFilterOptionsQueryKey } from './api';

const EmptyOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

const IncludeOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
];

/** The risk filter matches the row's displayed value, so Includes only. */
const RiskOperators = [{ value: FilterOperators.INCLUDES, label: 'Includes' }];

/** KIR-5099: the five AgentMesh risk levels, in AgentMesh's own order. */
const SKILL_RISK_OPTIONS = MCP_RISK_LEVELS.map(value => ({
  value,
  label: mcpRiskLevelLabels[value],
}));

/** What a row needs from the tab to render its Actions cell. */
export interface SkillColumnHandlers {
  onAllow: (row: SkillDataTableItem) => void;
  onRequestMark: (row: SkillDataTableItem) => void;
  isPending: (row: SkillDataTableItem) => boolean;
  /** False for a support user: they can read the tab but not write rules. */
  canEdit: boolean;
  /**
   * Shown on the disabled switch. Absent while the write gate is still
   * loading, so the tooltip does not claim a support restriction early.
   */
  readOnlyMessage?: string;
  /** Opens the Projects drilldown for the row. */
  onProjectsClick: (row: SkillDataTableItem) => void;
  /** Opens the Users drilldown for the row. */
  onUsersClick: (row: SkillDataTableItem) => void;
  /** Scope for the filter dropdowns. */
  organizationId: string;
  /** The table's pill; the dropdowns offer only values it shows. */
  listState: SkillListStateFilter;
  /** KIR-4701: set while a security group's choices are shown and edited. */
  group?: SkillGroupColumnHandlers;
}

/** A security group's Allow/Block actions on a skill row (KIR-4701). */
export interface SkillGroupColumnHandlers {
  name: string;
  onAllow: (row: SkillDataTableItem) => void;
  onRequestBlock: (row: SkillDataTableItem) => void;
  isPending: (row: SkillDataTableItem) => boolean;
}

type SkillColumn = ColumnConfig<SkillDataTableItem>;
/** The filterable arm of the union — the only one carrying `filterOptions`. */
type FilterableSkillColumn = Extract<SkillColumn, { filterable: true }>;

/**
 * Options for one server-backed dropdown. Every filterable column here is
 * DYNAMIC: skill names, repositories and users are all open-ended, so no
 * static list could enumerate them.
 */
function dynamicOptions(
  organizationId: string,
  listState: SkillListStateFilter,
  field: (typeof SKILL_FILTER_OPTION_FIELDS)[keyof typeof SKILL_FILTER_OPTION_FIELDS]
): FilterableSkillColumn['filterOptions'] {
  const extra = { listState };
  return {
    type: FilterOptionTypes.DYNAMIC,
    pagination: { mode: 'offset', limit: 10 },
    getQueryKey: params =>
      getSkillFilterOptionsQueryKey(organizationId, field, extra, params),
    queryFn: params =>
      fetchSkillFilterOptions(organizationId, field, extra, params),
    queryConfig: { staleTime: 2 * 60 * 1000 },
  };
}

/**
 * The five columns KIR-4699 specifies: Name, Projects, Users, Risk, Actions.
 * Risk is AgentMesh's verdict (KIR-5099), sortable and filterable.
 */
export function createSkillsColumns(
  handlers: SkillColumnHandlers
): ColumnConfig<SkillDataTableItem>[] {
  const organizationId = handlers.organizationId;

  return [
    {
      id: 'name',
      header: 'Name',
      accessor: 'displayName',
      width: 360,
      minWidth: 240,
      renderCell: ({ row }) => <NameCell row={row} />,
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      // No `EmptyOperators`: the list requires a non-null `skill_name`, so
      // Is Empty could only ever return nothing (KIR-4850 review).
      filterOperators: [
        ...IncludeOperators,
        { value: FilterOperators.CONTAINS, label: 'Contains' },
        { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
      ],
      statePersistence: { urlKey: SKILL_FILTER_URL_KEYS.name },
      filterOptions: dynamicOptions(
        organizationId,
        handlers.listState,
        SKILL_FILTER_OPTION_FIELDS.NAME
      ),
    },
    {
      id: 'projects',
      header: 'Projects',
      accessor: 'projects',
      width: 130,
      renderCell: ({ row }) => (
        <ProjectsCell row={row} onClick={() => handlers.onProjectsClick(row)} />
      ),
      sortable: true,
      sortField: 'projects',
      sortKey: 'p',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [...IncludeOperators, ...EmptyOperators],
      statePersistence: { urlKey: SKILL_FILTER_URL_KEYS.projects },
      filterOptions: dynamicOptions(
        organizationId,
        handlers.listState,
        SKILL_FILTER_OPTION_FIELDS.PROJECTS
      ),
    },
    {
      id: 'users',
      header: 'Users',
      accessor: 'users',
      width: 110,
      renderCell: ({ row }) => (
        <UsersCell row={row} onClick={() => handlers.onUsersClick(row)} />
      ),
      sortable: true,
      sortField: 'users',
      sortKey: 'u',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [...IncludeOperators, ...EmptyOperators],
      statePersistence: { urlKey: SKILL_FILTER_URL_KEYS.users },
      filterOptions: dynamicOptions(
        organizationId,
        handlers.listState,
        SKILL_FILTER_OPTION_FIELDS.USERS
      ),
    },
    {
      id: 'risk',
      header: 'Risk',
      accessor: 'risk',
      width: 140,
      renderCell: ({ row }) => (
        <SkillRiskCell risk={row.risk} reason={row.riskUnavailableReason} />
      ),
      sortable: true,
      sortField: 'risk',
      sortKey: 'r',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: RiskOperators,
      statePersistence: { urlKey: SKILL_FILTER_URL_KEYS.risk },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: SKILL_RISK_OPTIONS,
        render: (option, isSelected) => (
          <McpRiskFilterOption
            level={option.value as MCPRiskLevel}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: 'actions',
      header: handlers.group
        ? `Enforcement (${handlers.group.name})`
        : 'Actions',
      accessor: 'listState',
      width: handlers.group ? 300 : 200,
      minWidth: 180,
      clipOverflow: false,
      renderCell: ({ row }) => {
        const group = handlers.group;
        if (group) {
          return (
            <SkillGroupStateCell
              blocked={row.listState === 'marked_for_delete'}
              source={row.enforcementSource}
              groupName={group.name}
              name={row.displayName}
              onAllow={() => group.onAllow(row)}
              onRequestBlock={() => group.onRequestBlock(row)}
              pending={group.isPending(row)}
              canEdit={handlers.canEdit}
              readOnlyMessage={handlers.readOnlyMessage}
            />
          );
        }
        return (
          <SkillStateCell
            marked={row.listState === 'marked_for_delete'}
            name={row.displayName}
            onAllow={() => handlers.onAllow(row)}
            onRequestMark={() => handlers.onRequestMark(row)}
            pending={handlers.isPending(row)}
            disabled={!handlers.canEdit}
            readOnlyMessage={handlers.readOnlyMessage}
          />
        );
      },
      sortable: true,
      sortField: 'listState',
      sortKey: 's',
    },
  ];
}
